import { defineStore, acceptHMRUpdate } from 'pinia';
import {
    getSessionReplayEntities,
    getStarReplay,
    createSessionReplayEntity,
    starReplay,
    unstarReplay,
    updateReplayEntity,
} from '@/stores/api/session-replay.api';
import {
    getEntityKey,
    getReplaySettings,
    getSessionReplayRecordId,
    runReplayRecordingAggregation,
} from '@/session-replay/helpers/common.util';
import { canViewSessionReplayForApp } from '@/stores/helpers/auth.helper';
import values from 'lodash/values';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';

interface SessionReplayEntity {
    id?: string;
    starred?: boolean;
    watched?: boolean;
    recordingSessionId?: string;
    [key: string]: unknown;
}
interface ReplaySettings {
    [key: string]: unknown;
}

interface App {
    id: string;
    displayName?: string;
    [key: string]: unknown;
}

interface SessionReplayState {
    error: unknown;
    fetching: boolean;
    sessionReplayEntitiesById: Record<string, SessionReplayEntity>;
    // True only once loadAll has fetched the whole list; single-record writes do not count.
    hasLoadedAll: boolean;
    appReplaySettings: Record<string, ReplaySettings>;
}

const ENTITY_LOOKUP_RETRY_DELAY_MS = 500;

let loadAllPromise: Promise<unknown> | null = null;

export function __resetLoadAllPromiseForUnitTests (): void {
    loadAllPromise = null;
}

export function getInitialState (): SessionReplayState {
    return {
        error: null,
        fetching: false,
        sessionReplayEntitiesById: {},
        hasLoadedAll: false,
        appReplaySettings: {},
    };
}

export const useSessionReplayStore = defineStore('sessionReplay', {
    state: getInitialState,
    getters: {
        sessionReplayEntitiesMap: (state) => state.sessionReplayEntitiesById,
        sessionReplayEntities: (state) => values(state.sessionReplayEntitiesById),
        getReplayEntityAtKey: (state) => (key: string): SessionReplayEntity | undefined => state.sessionReplayEntitiesById[key],
        hasReplayEntities: (state) => !isEmpty(state.sessionReplayEntitiesById),
        liveReplayEnabledAppIds: (state) => Object.keys(state.appReplaySettings)
            .filter((appId) => get(state.appReplaySettings[appId], 'featureFlags.liveReplays', false)),
        // The liveReplays flag alone is not enough to join a session: the app must still have
        // session replay activated, exist in the apps store, and be viewable by the user. Anything gating live replay UI
        // reads this so the nav cannot advertise apps a liveness check would never find.
        liveReplayApps (): App[] {
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const { useAppsStore } = require('@/stores/apps.store');
            const appsStore = useAppsStore();

            return this.liveReplayEnabledAppIds
                .filter((appId: string) => appsStore.getApplicationFlag(appId, 'enableSessionReplay') && canViewSessionReplayForApp(appId))
                .map((appId: string) => appsStore.getAppById(appId))
                .filter(Boolean);
        },
    },
    actions: {
        async loadAll ({ reFetch = false }: { reFetch?: boolean } = {}): Promise<void> {
            if (loadAllPromise && !reFetch) {
                await loadAllPromise;

                return;
            }

            if (this.hasLoadedAll && !reFetch) {
                return;
            }

            this.fetching = true;
            loadAllPromise = getSessionReplayEntities();

            try {
                const replays = await loadAllPromise;

                this.sessionReplayEntitiesById = replays as Record<string, SessionReplayEntity>;
                this.hasLoadedAll = true;
            } catch (error) {
                loadAllPromise = null;
                this.error = error;
                throw error;
            } finally {
                this.fetching = false;
            }
        },
        // One record by its derived id, for callers that only need to know whether this session already has one.
        // A failure other than "no record" is retried once, then logged; the store is left untouched either way.
        async loadEntityForSession ({ recordingSessionId, appId }: { recordingSessionId: string; appId: string }): Promise<void> {
            const lookup = async () => {
                const replayId = await getSessionReplayRecordId({ recordingSessionId, appId });

                this.sessionReplayEntitiesById[recordingSessionId] = await getStarReplay(replayId) as SessionReplayEntity;
            };
            // 404 or 403 means this session has no record the current user can see.
            const isMissing = (error: unknown) => [404, 403].includes((error as { response?: { status?: number } })?.response?.status as number);

            try {
                await lookup();
            } catch (firstError) {
                if (isMissing(firstError)) return;

                await new Promise((resolve) => setTimeout(resolve, ENTITY_LOOKUP_RETRY_DELAY_MS));
                try {
                    await lookup();
                } catch (error) {
                    if (!isMissing(error)) console.error(error);
                }
            }
        },
        updateEntityAtKey ({ key, value }: { key: string; value: SessionReplayEntity }): void {
            this.sessionReplayEntitiesById[key] = value;
        },
        async createSessionReplayEntity ({ replayBody }: { replayBody: Record<string, unknown> }): Promise<void> {
            this.fetching = true;
            let body = replayBody;

            if (!body.recordingIds) {
                // eslint-disable-next-line @typescript-eslint/no-require-imports
                const { useAppsStore } = require('@/stores/apps.store');
                const replayRetentionDays = useAppsStore().getAppById(body.appId)?.recordingRetentionDays;
                body = await runReplayRecordingAggregation(body, replayRetentionDays);
            }

            try {
                const value = await createSessionReplayEntity(body);

                this.sessionReplayEntitiesById[getEntityKey(body)] = value as SessionReplayEntity;
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.fetching = false;
            }
        },
        async watchedReplay ({ replayBody }: { replayBody: Record<string, unknown> }): Promise<Record<string, unknown>> {
            this.fetching = true;
            let body = replayBody;

            if (!body.recordingIds) {
                // eslint-disable-next-line @typescript-eslint/no-require-imports
                const { useAppsStore } = require('@/stores/apps.store');
                const replayRetentionDays = useAppsStore().getAppById(body.appId)?.recordingRetentionDays;
                body = await runReplayRecordingAggregation(body, replayRetentionDays);
            }

            let updatedReplay: Record<string, unknown> = body;

            await updateReplayEntity(body)
                .then((res) => {
                    this.fetching = false;
                    this.sessionReplayEntitiesById[getEntityKey(body)] = res as SessionReplayEntity;

                    if (body.recordingSessionId) {
                        this.sessionReplayEntitiesById[body.recordingSessionId as string] = res as SessionReplayEntity;
                    }

                    updatedReplay = res as Record<string, unknown>;
                })
                .catch((error) => {
                    this.fetching = false;
                    this.error = error;
                    throw error;
                });

            return updatedReplay;
        },
        async starReplay ({ replayBody }: { replayBody: Record<string, unknown> }): Promise<void> {
            this.fetching = true;
            let replayEntity = this.getReplayEntityAtKey(replayBody?.recordingSessionId as string);

            if (!replayEntity) {
                await this.createSessionReplayEntity({ replayBody });
                replayEntity = this.getReplayEntityAtKey(replayBody?.recordingSessionId as string);
            }

            try {
                await starReplay(replayEntity!.id!);

                const entity = this.sessionReplayEntitiesById[getEntityKey(replayBody)];

                if (entity) {
                    entity.starred = true;
                }
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.fetching = false;
            }
        },
        async unstarReplay ({ replay }: { replay: SessionReplayEntity }): Promise<void> {
            const replayId = replay.id!;

            this.fetching = true;

            await unstarReplay(replayId)
                .then(() => {
                    this.fetching = false;

                    const entity = this.sessionReplayEntitiesById[getEntityKey(replay)];

                    if (entity) {
                        entity.starred = false;
                    }
                })
                .catch((error) => {
                    this.fetching = false;
                    this.error = error;
                    throw error;
                });
        },
        async getStarReplay ({ replayId }: { replayId: string }): Promise<unknown> {
            this.fetching = true;

            return getStarReplay(replayId)
                .then((starredStatus) => {
                    this.fetching = false;

                    return starredStatus;
                })
                .catch((error) => {
                    this.fetching = false;
                    this.error = error;
                    throw error;
                });
        },
        async loadReplaySettingsForApp ({ appId, reFetch }: { appId: string; reFetch?: boolean }): Promise<ReplaySettings> {
            if (reFetch || !this.appReplaySettings[appId]) {
                const replaySettings = await getReplaySettings({ appId });

                this.appReplaySettings[appId] = replaySettings as ReplaySettings;
            }

            return this.appReplaySettings[appId];
        },
        setReplaySettingsForApp ({ appId, replaySettings }: { appId: string; replaySettings: ReplaySettings }): void {
            this.appReplaySettings[appId] = replaySettings;
        },
        // Live replay enablement is per app, so every replay-enabled app's settings are needed to
        // know whether the subscription can use live replays at all.
        async loadReplaySettingsForReplayEnabledApps ({ reFetch = false }: { reFetch?: boolean } = {}): Promise<void> {
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const { useAppsStore } = require('@/stores/apps.store');
            const appsStore = useAppsStore();
            const appIds: string[] = appsStore.list
                .filter(({ id }: { id: string }) => appsStore.getApplicationFlag(id, 'enableSessionReplay') && canViewSessionReplayForApp(id))
                .map(({ id }: { id: string }) => id);

            await Promise.all(appIds.map(async (appId: string) => {
                try {
                    await this.loadReplaySettingsForApp({ appId, reFetch });
                } catch (error) {
                    console.error(`Unable to fetch replay settings for app ${appId}:`, error);
                }
            }));
        },
        async handleStarChange (replayBody: Record<string, unknown>): Promise<Record<string, unknown>> {
            if (get(replayBody, 'starred', false)) {
                try {
                    await this.starReplay({ replayBody });

                    const replayEntity = this.getReplayEntityAtKey(replayBody?.recordingSessionId as string);

                    if (!replayEntity || !replayEntity.id) {
                        throw new Error();
                    }

                    return { ...replayBody, id: replayEntity.id };
                } catch {
                    return { ...replayBody, starred: false, errorType: 'star' };
                }
            }

            try {
                await this.unstarReplay({ replay: replayBody as SessionReplayEntity });

                return replayBody;
            } catch {
                return { ...replayBody, starred: true, errorType: 'unstar' };
            }
        },
        async handleWatchedReplay (replayBody: Record<string, unknown>): Promise<Record<string, unknown>> {
            try {
                await this.watchedReplay({ replayBody });

                const { id: replayEntityId } = this.getReplayEntityAtKey(replayBody?.recordingSessionId as string) ?? {};

                if (!replayEntityId) {
                    throw new Error();
                }

                return { ...replayBody, id: replayEntityId };
            } catch {
                return { ...replayBody, watched: false };
            }
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useSessionReplayStore, module.hot));
}
